import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/workflows/user-workflows/{userWorkflowId}/pacer-packet-preview';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['params'] & Partial<THIS_API['query']>;
type Output = THIS_API['response'];

/**
 * SWR-based hook for fetching the PACER packet preview for a user workflow.
 * Returns rows with required/missing status, plus aggregate counts and sync readiness.
 */
export const usePacerPacketPreview = (
  params: Input | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = useMemo((): SwrKey => {
    if (!params?.userWorkflowId) return null;
    const { userWorkflowId, chapter, joint } = params;
    const path = buildPath<THIS_API>(PATH_TEMPLATE, { userWorkflowId });
    return [path, { params: { chapter, joint } }];
  }, [params]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches the PACER packet preview for a user workflow.
 * @deprecated Please use `usePacerPacketPreview` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getPacerPacketPreview = async ({ userWorkflowId, chapter, joint }: Input): Promise<Output> => {
  const path = buildPath<THIS_API>(PATH_TEMPLATE, { userWorkflowId });
  const response = await noodleApiClient.get(path, { params: { chapter, joint } });
  return response.data;
};

export default getPacerPacketPreview;
